import { useCallback } from 'react';
import { TRACKING_CONFIG } from '../config/tracking';
import { LeadData } from '../types';

// IDs desde configuración
const GTM_ID = TRACKING_CONFIG.GTM_ID;
const GA_MEASUREMENT_ID = TRACKING_CONFIG.GA_MEASUREMENT_ID;
const FB_PIXEL_ID = TRACKING_CONFIG.FB_PIXEL_ID;

interface TrackingEvent {
  event: string;
  category?: string;
  action?: string;
  label?: string;
  value?: number;
  [key: string]: unknown;
}

type FormType = 'hero' | 'final' | 'demo' | 'trial';

export const useTracking = () => {
  // Google Tag Manager
  const pushToGTM = useCallback((dataLayer: Record<string, unknown>) => {
    if (!TRACKING_CONFIG.ENABLE_TRACKING) {
      if (TRACKING_CONFIG.DEBUG_MODE) {
        console.log('GTM Event (disabled):', dataLayer);
      }
      return;
    }

    if (
      typeof window !== 'undefined' &&
      (window as unknown as Record<string, unknown>).dataLayer
    ) {
      (
        (window as unknown as Record<string, unknown>).dataLayer as unknown[]
      ).push(dataLayer);
      if (TRACKING_CONFIG.DEBUG_MODE) {
        console.log('GTM Event pushed:', dataLayer);
      }
    } else {
      if (TRACKING_CONFIG.DEBUG_MODE) {
        console.warn('GTM dataLayer not available');
      }
    }
  }, []);

  // Google Analytics 4
  const trackGA4Event = useCallback(
    (eventName: string, parameters: Record<string, unknown> = {}) => {
      if (!TRACKING_CONFIG.ENABLE_TRACKING) {
        if (TRACKING_CONFIG.DEBUG_MODE) {
          console.log('GA4 Event (disabled):', eventName, parameters);
        }
        return;
      }

      if (
        typeof window !== 'undefined' &&
        (window as unknown as Record<string, unknown>).gtag
      ) {
        (
          (window as unknown as Record<string, unknown>).gtag as (
            ...args: unknown[]
          ) => void
        )('event', eventName, parameters);
        if (TRACKING_CONFIG.DEBUG_MODE) {
          console.log('GA4 Event tracked:', eventName, parameters);
        }
      } else {
        if (TRACKING_CONFIG.DEBUG_MODE) {
          console.warn('GA4 gtag not available');
        }
      }
    },
    []
  );

  // Facebook Pixel
  const trackFBPixel = useCallback(
    (eventName: string, parameters: Record<string, unknown> = {}) => {
      if (!TRACKING_CONFIG.ENABLE_TRACKING) {
        if (TRACKING_CONFIG.DEBUG_MODE) {
          console.log('FB Pixel Event (disabled):', eventName, parameters);
        }
        return;
      }

      if (
        typeof window !== 'undefined' &&
        (window as unknown as Record<string, unknown>).fbq
      ) {
        (
          (window as unknown as Record<string, unknown>).fbq as (
            ...args: unknown[]
          ) => void
        )('track', eventName, parameters);
        if (TRACKING_CONFIG.DEBUG_MODE) {
          console.log('FB Pixel Event tracked:', eventName, parameters);
        }
      } else {
        if (TRACKING_CONFIG.DEBUG_MODE) {
          console.warn('FB Pixel not available');
        }
      }
    },
    []
  );

  // Eventos específicos del negocio
  const trackFormView = useCallback(
    (formType: FormType, source: string) => {
      const event: TrackingEvent = {
        event: 'form_view',
        category: 'engagement',
        action: 'form_view',
        label: formType,
        form_type: formType,
        source: source,
        timestamp: new Date().toISOString(),
      };

      pushToGTM(event);
      trackGA4Event('form_view', {
        form_type: formType,
        source: source,
      });
    },
    [pushToGTM, trackGA4Event]
  );

  const trackFormStart = useCallback(
    (formType: FormType, source: string) => {
      const event: TrackingEvent = {
        event: 'form_start',
        category: 'engagement',
        action: 'form_start',
        label: formType,
        form_type: formType,
        source: source,
        timestamp: new Date().toISOString(),
      };

      pushToGTM(event);
      trackGA4Event('form_start', {
        form_type: formType,
        source: source,
      });
    },
    [pushToGTM, trackGA4Event]
  );

  const trackFormSubmit = useCallback(
    (formType: FormType, source: string, leadData: LeadData) => {
      const event: TrackingEvent = {
        event: 'form_submit',
        category: 'conversion',
        action: 'form_submit',
        label: formType,
        form_type: formType,
        source: source,
        lead_data: leadData,
        timestamp: new Date().toISOString(),
      };

      pushToGTM(event);
      trackGA4Event('form_submit', {
        form_type: formType,
        source: source,
        lead_business: leadData.salon || leadData.clinic || leadData.restaurant,
        vertical: leadData.vertical || 'hair-beauty',
      });
      trackFBPixel('Lead', {
        content_name: `Form ${formType}`,
        content_category: 'lead_generation',
        value: 1,
        currency: 'EUR',
      });
    },
    [pushToGTM, trackGA4Event, trackFBPixel]
  );

  const trackFormError = useCallback(
    (formType: FormType, source: string, errorType: string) => {
      const event: TrackingEvent = {
        event: 'form_error',
        category: 'error',
        action: 'form_error',
        label: errorType,
        form_type: formType,
        source: source,
        error_type: errorType,
        timestamp: new Date().toISOString(),
      };

      pushToGTM(event);
      trackGA4Event('form_error', {
        form_type: formType,
        source: source,
        error_type: errorType,
      });
    },
    [pushToGTM, trackGA4Event]
  );

  const trackPageView = useCallback(
    (pageName: string, source: string) => {
      const event: TrackingEvent = {
        event: 'page_view',
        category: 'navigation',
        action: 'page_view',
        label: pageName,
        page_name: pageName,
        source: source,
        timestamp: new Date().toISOString(),
      };

      pushToGTM(event);
      trackGA4Event('page_view', {
        page_name: pageName,
        source: source,
      });
    },
    [pushToGTM, trackGA4Event]
  );

  const trackCTAClick = useCallback(
    (ctaType: string, source: string) => {
      const event: TrackingEvent = {
        event: 'cta_click',
        category: 'engagement',
        action: 'cta_click',
        label: ctaType,
        cta_type: ctaType,
        source: source,
        timestamp: new Date().toISOString(),
      };

      pushToGTM(event);
      trackGA4Event('cta_click', {
        cta_type: ctaType,
        source: source,
      });
    },
    [pushToGTM, trackGA4Event]
  );

  return {
    // Métodos de tracking
    trackFormView,
    trackFormStart,
    trackFormSubmit,
    trackFormError,
    trackPageView,
    trackCTAClick,

    // Métodos directos
    pushToGTM,
    trackGA4Event,
    trackFBPixel,

    // IDs para configuración
    GTM_ID,
    GA_MEASUREMENT_ID,
    FB_PIXEL_ID,
  };
};
